<template>
  <div
    v-if="!isLoadingDetail && !isUploadingNewRainDetail"
    class="modern-campaign-detail animation-slide"
  >
    <!-- Edit Actions -->
    <HeaderSection
      :detail="detail"
      :isEditing="isEditing"
      :isGeneratingRain="isGeneratingRain"
      @start-editing="startEditing"
      @clone-rain="cloneRain"
      @generate-rain="generateRain"
    />

    <div class="freebet-alert-banner" v-if="!totalRain">
      <i class="fa-solid fa-circle-exclamation alert-icon"></i>
      <span class="alert-text text-black">
        To make the Rain free bet available, you must have at least one
        associated rain. You can generate them via the "Generate Rain" button.
      </span>
    </div>

    <SummaryCampaign
      v-if="skinId && campaignId && totalRain > 0 && !isEditing && 0"
      title="Campaign Summary"
      :cardsData="cardsData"
      :cardsInfo="[
        {
          label: 'Total Bets',
          icon: 'fa-solid fa-layer-group',
          type: 'totalBets',
          property: 'totalBets',
        },
        {
          label: 'Total Wins',
          icon: 'fa-solid fa-trophy',
          type: 'totalWins',
          property: 'totalWins',
        },
        {
          label: 'Net Result',
          icon: 'fa-solid fa-chart-line',
          type: 'net',
        },
      ]"
      :campaign_id="campaignId"
      :currency_code="detail.currency_code ?? 'EUR'"
      :skin_id="skinId"
      :showCurrencySwitcher="true"
      :showEuro="true"
      :dataLoaded="dataLoaded"
      @toggle-currency="showEuro = $event"
    />

    <div v-if="detail && editableDetail" class="detail-container">
      <div class="detail-sections">
        <FieldSection
          :detail="detail"
          :editableDetail="editableDetail"
          :isEditing="isEditing"
          :keys="[
            'title',
            'description',
            'note',

            'skin_id',
            'games',
            'total_rain',
            'is_enabled',
          ]"
          title="General Information"
          icon="clipboard"
          :getIconClass="getIconClass"
          :iconColor="'text-primary'"
          :formatLabel="formatLabel"
          :formatVal="formatVal"
          :typeOptions="typeOptions"
          :allowedSkins="allowedSkins"
          :validateHourInput="validateHourInput"
        />

        <FieldSection
          :detail="detail"
          :editableDetail="editableDetail"
          :isEditing="isEditing"
          :keys="[
            'start_date',
            'end_date',
            'start_time',
            'end_time',
            'duration',
            'deadline',
            'created_at',
            'updated_at',
          ]"
          title="Schedule & Timing"
          icon="calendar-days"
          :getIconClass="getIconClass"
          :iconColor="'text-info'"
          :formatLabel="formatLabel"
          :formatVal="formatVal"
          :typeOptions="typeOptions"
          :allowedSkins="allowedSkins"
          :validateHourInput="validateHourInput"
        />

        <FieldSection
          :detail="detail"
          :editableDetail="editableDetail"
          :isEditing="isEditing"
          :keys="[
            'max_players',
            'count_day_freebet',
            'claim_limitation_period',
            'claim_limitation_daily',

            'quantity',
          ]"
          title="Configuration"
          icon="gears"
          :getIconClass="getIconClass"
          :iconColor="'text-success'"
          :formatLabel="formatLabel"
          :formatVal="formatVal"
          :typeOptions="typeOptions"
          :allowedSkins="allowedSkins"
          :validateHourInput="validateHourInput"
        />
        <!-- currency -->
        <div>
          <div
            class="w-full bg-white border border-gray-100 shadow-sm rounded-2xl mb-6 overflow-hidden"
          >
            <div
              class="flex items-center gap-1 px-3 py-3 border-b border-gray-100 bg-gray-50/50 flex items-center gap-1 w-full"
            >
              <div class="flex items-center justify-center w-5 h-5">
                <i class="fa-solid fa-dollar-sign text-sm"></i>
              </div>
              <h2
                class="font-bold text-gray-700 text-[11px] uppercase tracking-wider"
              >
                Currency
              </h2>
            </div>

            <div class="divide-y divide-gray-50">
              <div
                v-for="(curr, index) in detail.currencies"
                :key="index"
                class="py-2.5 px-3"
              >
                <div
                  class="mb-6 flex items-center justify-center gap-2 relative"
                >
                  <span
                    class="px-3 py-1 rounded-full bg-gray-900 text-white text-[11px] font-black tracking-wider uppercase relative z-[9999]"
                  >
                    {{ formatVal("currency_code", curr.currency_code) }}
                  </span>
                  <div
                    class="h-[1px] flex-grow bg-gray-50 absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 w-full bg-gray-200"
                  ></div>
                </div>

                <div
                  class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-6"
                >
                  <div
                    v-for="field in [
                      'amount',
                      'min_balance',
                      'day_budget',
                      'total_budget',
                    ]"
                    :key="field"
                    class="flex flex-col gap-1 group"
                  >
                    <div
                      class="text-slate-500 text-[10px] font-bold uppercase tracking-tight"
                    >
                      <i
                        :class="[
                          getIconClass(field),
                          'text-[11px] text-amber-500',
                        ]"
                      ></i>
                      <span
                        class="text-slate-500 text-[10px] font-bold uppercase tracking-tight"
                      >
                        {{ formatLabel(field) }}
                      </span>
                    </div>

                    <div class="flex items-baseline gap-1.5">
                      <span
                        class="text-[15px] font-bold text-gray-800 tracking-tight"
                      >
                        {{ formatVal(field, curr[field]) }}
                      </span>
                      <span
                        v-if="field.includes('budget')"
                        class="text-[10px] font-bold text-gray-400"
                      >
                        {{ curr.currency_code }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- end currency -->
        </div>
      </div>

      <div
        class="d-flex justify-content-center editing-cta-btn align-items-center w-100 p-3 gap-2"
        v-if="isEditing"
      >
        <button class="btn create-btn text-white" @click="saveChanges">
          {{ $t("util.save") }}
          <i class="bi bi-floppy ml-2 fs-6 text-white"></i>
        </button>
        <button @click="cancelEditing" class="btn text-white cancel-btn">
          {{ $t("util.cancel") }}
        </button>
      </div>
    </div>
  </div>
  <div
    class="w-100 d-flex align-items-center justify-content-center h-auto pt-5"
    v-else
  >
    <Loader />
  </div>
  <RainTimeModal
    v-if="isRainTimeModalOpen && rainTimeGenerated.length > 0"
    :rainTimeGenerated="rainTimeGenerated"
    :toggleModal="toggleRainTimeModal"
    :detail="detail"
  />
</template>

<script>
//Utility
import { format } from "date-fns";
import { mapGetters } from "vuex";

//axios method to make api call
import { getFetch, putFetch } from "@/api/axiosClient";

// Reusable component
import Swal from "sweetalert2";
import Loader from "../Loader.vue";

//RainTime modal to generate rain
import RainTimeModal from "../RainTimeModal.vue";

//Rain Info Child Components
import HeaderSection from "./HeaderSection.vue";
import FieldSection from "./FieldSection.vue";
import BudgetCard from "./BudgetCard.vue";
import SummaryCampaign from "../SummaryCampaign.vue";

export default {
  name: "RainCampaignDetail",
  components: {
    Loader,
    RainTimeModal,
    HeaderSection,
    FieldSection,
    BudgetCard,
    SummaryCampaign,
  },
  props: {
    detail: {
      type: Object,
      default: () => ({}),
    },
    campaignId: {
      type: String,
      required: true,
    },
    isLoadingDetail: {
      type: Boolean,
      default: false,
    },
    saveDetail: Function,
  },
  computed: {
    ...mapGetters(["allowedSkins", "allowedGamesFreebet"]),
    skinId() {
      return this.detail?.skin_id ?? null;
    },
    totalRain() {
      return this.detail?.total_rain ?? 0;
    },
  },
  data() {
    return {
      canShowSummary: false,
      gameChoose: {},
      typeOptions: {
        all: "All",
        cashout_1: "Cashout 1",
        cashout_2: "Cashout 2",
        oddeven: "Odd/Even",
        ranges: "Ranges",
        cashout_1_cashout_2: "Cashout 1 + Cashout 2",
        cashout_1_oddeven: "Cashout 1 + Odd/Even",
        cashout_1_ranges: "Cashout 1 + Ranges",
        cashout_2_oddeven: "Cashout 2 + Odd/Even",
        cashout_2_ranges: "Cashout 2 + Ranges",
        oddeven_ranges: "Odd/Even + Ranges",
        cashout_1_cashout_2_oddeven: "Cashout 1 + Cashout 2 + Odd/Even",
        cashout_1_cashout_2_ranges: "Cashout 1 + Cashout 2 + Ranges",
        cashout_1_oddeven_ranges: "Cashout 1 + Odd/Even + Ranges",
        cashout_2_oddeven_ranges: "Cashout 2 + Odd/Even + Ranges",
      },
      editableDetail: {},
      isEditing: false,
      isGeneratingRain: false,
      isUploadingNewRainDetail: false,
      isRainTimeModalOpen: false,
      cardsData: [],
      dataLoaded: false,
    };
  },
  async beforeCreate() {},
  async mounted() {
    // setTimeout(() => {
    //   this.canShowSummary = true;
    // }, 5000);
    this.getCost();
  },
  watch: {
    "editableDetail.day_budget": "calculateTotalBudget",
    "editableDetail.start_date": "calculateTotalBudget",
    "editableDetail.end_date": "calculateTotalBudget",
    "editableDetail.max_players": "calculateTotalBudget",
    "editableDetail.count_day_freebet": "calculateTotalBudget",
    "editableDetail.amount": "calculateTotalBudget",
    "editableDetail.quantity": "calculateTotalBudget",
    detail: "getCost",
  },
  methods: {
    async getCost() {
      return;
      if (this.detail?.skin_id && this.campaignId) {
        try {
          let query = {
            skin_id: this.detail?.skin_id,
          };

          if (this.campaignId) {
            query.campaign_id = this.campaignId;
          }

          let data = await getFetch("/freebets/calculate-costs", query);

          this.cardsData = data;
          console.log(this.cardsData);
          this.dataLoaded = true;
        } catch (error) {
          console.error("Error fetching cost data:", error);
          this.dataLoaded = true;
        }
      }
    },
    formatCurrency(amount) {
      if (!amount && amount !== 0) return "0.00";
      return `${parseFloat(amount).toFixed(2)}`;
    },

    cloneRain() {
      setTimeout(() => {
        window.open(
          "/slot/rains/create?campaign_id=" +
            this.campaignId +
            "&skin_id=" +
            this.detail?.skin_id,
          "_blank",
        );
      });
    },
    async calculateTotalBudget() {
      let daybudget =
        parseFloat(this.editableDetail.max_players) *
        parseFloat(this.editableDetail.count_day_freebet) *
        parseFloat(this.editableDetail.amount);

      const totalDay = daybudget;
      this.editableDetail.day_budget = Number.isFinite(totalDay) ? totalDay : 0;

      const onlyDate = (datetimeString) => {
        const [datePart] = datetimeString.split(" ");
        return new Date(datePart + "T00:00:00");
      };

      const start = onlyDate(this.editableDetail.start_date);
      const end = onlyDate(this.editableDetail.end_date);

      const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;

      if (
        this.editableDetail.quantity === 0 ||
        this.editableDetail.quantity === null ||
        this.editableDetail.quantity === ""
      ) {
        this.editableDetail.quantity = 1;
      }

      const total =
        days * this.editableDetail.day_budget * this.editableDetail.quantity;
      this.editableDetail.total_budget = Number.isFinite(total) ? total : 0;
    },

    formatLabel(key) {
      return key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
    },

    formatVal(key, value) {
      if (value === null || value === undefined) return "N/A";

      switch (key) {
        case "is_enabled":
          return value ? "Enabled" : "Disabled";
        case "created_at":
        case "updated_at":
          return format(new Date(value), "dd/MM/yyyy HH:mm");
        case "start_date":
        case "end_date":
          return format(new Date(value), "dd/MM/yyyy");
        case "start_time":
        case "end_time":
          return value.toString().includes(":") ? value : `${value}:00`;
        case "amount":
        case "min_cashout":
        case "min_balance":
        case "day_budget":
        case "total_budget":
          return this.formatCurrency(value);
        case "type":
          return this.typeOptions[value] || value;
        default:
          return value.toString();
      }
    },

    getIconClass(key) {
      const iconMap = {
        title: "bi bi-card-heading",
        description: "bi bi-card-text",
        note: "bi bi-sticky",
        type: "bi bi-tags",
        skin_id: "bi bi-palette",
        total_rain: "bi bi-cloud",
        _id: "bi bi-hash",
        is_enabled: "bi bi-toggle-on",
        start_date: "bi bi-calendar-date",
        end_date: "bi bi-calendar-check",
        start_time: "bi bi-clock",
        end_time: "bi bi-clock-fill",
        duration: "bi bi-hourglass-split",
        deadline: "bi bi-alarm",
        created_at: "bi bi-calendar-plus",
        updated_at: "bi bi-calendar-event",
        max_players: "bi bi-people",
        count_day_freebet: "bi bi-gift",
        amount: "bi bi-currency-dollar",
        currency_code: "bi bi-cash-coin",
        claim_limitation_period: "bi bi-calendar-range",
        claim_limitation_daily: "bi bi-calendar-day",
        min_cashout: "bi bi-rocket-takeoff-fill",
        min_balance: "bi bi-wallet2",
        total_budget: "bi bi-wallet",
        day_budget: "bi bi-calendar-day",
        min_wager: "bi bi-bullseye",
        quantity: "bi bi-123",
      };
      return iconMap[key] || "bi bi-info-circle";
    },

    handleNumericInput(event, key) {
      let input = event.target.value;
      if (input === "") input = "0";
      input = input.replace(/,/g, ".");
      input = input.replace(/[^0-9.]/g, "");

      const parts = input.split(".");
      if (parts.length > 2) {
        input = parts[0] + "." + parts.slice(1).join("");
      }

      if (input.includes(".")) {
        const [intPart, decimalPart] = input.split(".");
        input = intPart + "." + decimalPart.slice(0, 2);
      }

      this.editableDetail[key] = input;
    },

    validateHourInput(key) {
      let rawValue = this.editableDetail[key];

      let hourPart = String(rawValue).split(":")[0].replace(/\D/g, "");

      let hour = parseInt(hourPart, 10);
      if (isNaN(hour)) hour = 0;

      if (hour < 0) hour = 0;
      if (hour > 23) hour = 23;

      const formatted = hour.toString().padStart(2, "0") + ":00";

      this.editableDetail[key] = formatted;

      const start = parseInt(this.editableDetail.start_time?.split(":")[0], 10);
      const end = parseInt(this.editableDetail.end_time?.split(":")[0], 10);

      if (start === 0 && end === 0) {
        this.editableDetail.end_time = "01:00";
      }

      if (start === end) {
        this.editableDetail.end_time =
          (start < 23 ? (start + 1).toString().padStart(2, "0") : "23") + ":00";
      }

      if (end < start) {
        this.editableDetail.end_time =
          (start < 23 ? (start + 1).toString().padStart(2, "0") : "23") + ":00";
      }

      if (start === 23) {
        this.editableDetail.start_time = "22:00";
      }
    },
    startEditing() {
      this.isEditing = true;
      this.editableDetail = this.detail;
    },

    async saveChanges() {
      let dataToSend = { ...this.editableDetail };

      const cleanDate = (dateString) => {
        return dateString.split(" ")[0];
      };

      const convertDateToISOString = (dateString, type = "start") => {
        const cleanedDate = cleanDate(dateString);
        const [year, month, day] = cleanedDate.split("-");
        const time = type === "start" ? "T00:00:00.000Z" : "T23:59:59.999Z";
        const date = new Date(`${year}-${month}-${day}${time}`);
        return isNaN(date) ? null : date.toISOString();
      };

      const dateFields = [
        { key: "start_date", type: "start" },
        { key: "end_date", type: "end" },
      ];

      for (const { key, type } of dateFields) {
        if (dataToSend[key]) {
          const iso = convertDateToISOString(dataToSend[key], type);
          if (iso) {
            dataToSend[key] = iso;
          } else {
            Swal.fire({
              title: "Error!",
              text: `Invalid date format in ${key.replace("_", " ")}.`,
              icon: "error",
            });
            return;
          }
        }
      }

      ["start_time", "end_time"].forEach((timeField) => {
        if (dataToSend[timeField] !== undefined) {
          let time = dataToSend[timeField].trim();

          if (/^\d{1,2}:\d{2}:\d{2}$/.test(time)) {
            const [hh, mm] = time.split(":");
            dataToSend[timeField] = `${hh.padStart(2, "0")}:${mm}`;
          } else if (/^\d{1,2}:\d{2}$/.test(time)) {
            const [hh, mm] = time.split(":");
            dataToSend[timeField] = `${hh.padStart(2, "0")}:${mm}`;
          } else if (/^\d{1,2}$/.test(time)) {
            dataToSend[timeField] = `${time.padStart(2, "0")}:00`;
          } else {
            dataToSend[timeField] =
              timeField === "start_time" ? "00:00" : "23:59";
          }
        }
      });

      const numericFields = [
        "day_budget",
        "min_cashout",
        "min_balance",
        "total_budget",
        "amount",
      ];
      numericFields.forEach((field) => {
        if (dataToSend[field] !== undefined) {
          dataToSend[field] = parseFloat(dataToSend[field]).toFixed(2);
        }
      });

      this.isUploadingNewRainDetail = true;

      try {
        await putFetch(`/campaigns/${this.detail?._id}`, dataToSend);

        Swal.fire("Success!", "The Rain detail has been updated.").then(() => {
          window.location.reload();
        });
      } catch (error) {
        console.error("Error updating Rain detail:", error);
        Swal.fire({
          title: "Error",
          text: "Failed to update the Rain detail.",
          icon: "error",
        });
      } finally {
        this.isUploadingNewRainDetail = false;
      }
    },
    async generateRain() {
      const id = this.detail?._id;
      if (!id) {
        Swal.fire({
          title: "Errore",
          text: "ID della campagna non disponibile.",
          icon: "error",
        });
        return;
      }

      try {
        this.isGeneratingRain = true;
        const response = await getFetch(`/campaigns/generaterain/${id}`);
        if (
          Array.isArray(response) &&
          response.length > 0 &&
          response[0].start_at &&
          response[0].end_at
        ) {
          this.rainTimeGenerated = response;
          this.isRainTimeModalOpen = true;
        } else {
          this.rainTimeGenerated = [];
          Swal.fire({
            title: "Attenzione",
            text: "La generazione dei rain times non ha restituito dati validi.",
            icon: "warning",
          });
        }
      } catch (error) {
        console.error("Errore durante la generazione dei rain:", error);
        Swal.fire({
          title: "Errore",
          text: "Si è verificato un errore durante la generazione dei rain times.",
          icon: "error",
        });
        this.isRainTimeModalOpen = false;
      } finally {
        this.isGeneratingRain = false;
      }
    },
    toggleRainTimeModal() {
      this.isRainTimeModalOpen = !this.isRainTimeModalOpen;
      if (!this.isRainTimeModalOpen) {
        this.rainTimeGenerated = [];
      }
    },

    cancelEditing() {
      this.isEditing = false;
      this.editableDetail = this.detail;
    },
  },
};
</script>

<style lang="scss" scoped>
@import url("@/assets/styles/components/rainInfo.scss");
</style>
